// Hints' ears on the web (docs/features/stream-hints.md › Voice): the Web Speech API, on-device only. A browser that
// can't promise its speech stays on this device (Chrome's `processLocally`, checked with `SpeechRecognition.available`)
// is never used: the label field is the way in then. Started only from a tap (the Hints button, or the mic in the
// row); each final result is one utterance, handed on with the recognizer's confidence; it listens until stopped.
//
// `await localSpeech()` → null when on-device speech can be used, else the words why not.
// `listen({ onUtterance, onProblem, onState })` → `{ stop() }`.

const SR = () => globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition
const lang = () => globalThis.navigator?.language || 'en-US'

export async function localSpeech() {
  const R = SR()
  if (!R) return 'No speech in this browser — type the label'
  if (typeof R.available !== 'function') return 'This browser’s speech isn’t on-device — type the label'
  try {
    const a = await R.available({ langs: [lang()], processLocally: true })
    return a === 'available' ? null : 'On-device speech isn’t installed here — type the label'
  } catch { return 'This browser’s speech isn’t on-device — type the label' }
}

export function listen({ onUtterance, onProblem = () => {}, onState = () => {} }) {
  const R = SR()
  let stopped = false, r = null
  const start = () => {
    if (stopped) return
    r = new R()
    r.lang = lang()
    r.continuous = true
    r.interimResults = false
    r.processLocally = true   // never a server
    r.onresult = (e) => {
      for (let i = e.resultIndex ?? 0; i < e.results.length; i++) {
        const res = e.results[i]
        if (!res.isFinal) continue
        const best = res[0]
        const text = String(best?.transcript || '').trim()
        if (text) onUtterance(text, typeof best.confidence === 'number' && best.confidence > 0 ? best.confidence : null)
      }
    }
    r.onerror = (e) => { if (e.error === 'not-allowed' || e.error === 'service-not-allowed' || e.error === 'language-not-supported') { stopped = true; onProblem('Speech: ' + e.error + ' — type the label') } }
    r.onend = () => { if (!stopped) setTimeout(start, 250); else onState(false) }   // a browser ends after a silence: listen again
    try { r.start(); onState(true) } catch (e) { stopped = true; onProblem(e.message); onState(false) }
  }
  start()
  return { stop() { stopped = true; try { r?.stop() } catch {} ; onState(false) } }
}
